import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/ui/button'
import { TablePagination } from '@/components/ui/pagination'
import { SkeletonRows } from '@/components/ui/skeleton'
import { ClaimsTable } from '../../../../components/ClaimsTable'
import { type InternalClaim, useInternalClaims } from '../../../../models/claim'
import { useFeatureFlag } from '../../../../models/flag'
import { OrgRole, useOrganisation } from '../../../../models/user'
import { ClaimAdjusterDialog } from '../Internal/ClaimAdjusterDialog'
import { ClaimLifecycleActions } from '../Internal/ClaimLifecycleActions'
import { ClaimLifecycleLinks } from '../Internal/ClaimLifecycleLinks'
import { ClaimOutcomeDialog } from '../Internal/ClaimOutcomeDialog'

export function ClaimsTab({ orgId }: { orgId: string }) {
  const navigate = useNavigate()
  // The tab outlives a move to another customer, who starts on their first page.
  const [paging, setPaging] = useState({ orgId, page: 1 })
  const page = paging.orgId === orgId ? paging.page : 1
  const { claims, count, hasNextPage, hasPreviousPage, isLoading } = useInternalClaims({ orgId, page })
  const { organisation } = useOrganisation(orgId)
  const businessOwner = organisation?.users.find((u) => u.orgUser.role === OrgRole.BUSINESS_OWNER)
  const lifecycleEnabled = useFeatureFlag('ENABLE_CLAIM_LIFECYCLE')
  // Held by the tab, not the row: a list refetch would otherwise unmount them mid-entry.
  const [carrierFor, setCarrierFor] = useState<InternalClaim | null>(null)
  const [outcomeFor, setOutcomeFor] = useState<InternalClaim | null>(null)

  function createClaim() {
    if (!businessOwner) return
    // Create the claim on behalf of the business owner via the intake wizard.
    void navigate(`/insure/internal/claims/new?org_id=${orgId}&user_id=${businessOwner.id}`)
  }

  const claimFor = (row: { id: string }) => claims.find((c: InternalClaim) => c.id === row.id)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={createClaim} disabled={!businessOwner}>
          <Plus className="size-4" /> Create claim
        </Button>
      </div>

      {isLoading ? (
        <SkeletonRows />
      ) : (
        <ClaimsTable
          claims={claims}
          isLoading={isLoading}
          emptyMessage="No claims for this customer."
          getEditHref={(claim) => `/insure/internal/claims/${claim.id}?org_id=${orgId}`}
          allowWithdraw={lifecycleEnabled}
          withdrawOrgId={orgId}
          rowActions={
            lifecycleEnabled
              ? (row) => {
                  const claim = claimFor(row)
                  return claim ? <ClaimLifecycleLinks claim={claim} onOpenCarrier={setCarrierFor} /> : null
                }
              : undefined
          }
          renderStatus={
            lifecycleEnabled
              ? (row) => {
                  const claim = claimFor(row)
                  return claim ? (
                    <ClaimLifecycleActions
                      claim={claim}
                      onOpenCarrier={setCarrierFor}
                      onAskOutcome={setOutcomeFor}
                    />
                  ) : null
                }
              : undefined
          }
        />
      )}

      <TablePagination
        page={page}
        count={count}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onPageChange={(next) => setPaging({ orgId, page: next })}
        itemLabel="claim"
      />

      {carrierFor && (
        <ClaimAdjusterDialog claim={carrierFor} open onOpenChange={(open) => !open && setCarrierFor(null)} />
      )}

      {outcomeFor && (
        <ClaimOutcomeDialog claim={outcomeFor} open onOpenChange={(open) => !open && setOutcomeFor(null)} />
      )}
    </div>
  )
}
